import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Company, CustomerRequiredFile } from '@app/models';
import { Broadcaster, CompanyService, EventTypes, SharedService, Toaster } from '@app/services';
import { CreateCompanyModalComponent } from '@app/shared/modals/create-company-modal/create-company-modal.component';
import { NbMediaBreakpointsService, NbThemeService } from '@nebular/theme';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DxDataGridComponent } from 'devextreme-angular';
import { LocalStorage } from 'ngx-webstorage';
import { tap, first } from 'rxjs/operators';
import { Observable, of, from } from 'rxjs';
import { environment } from '@env/environment';


@Component({
  selector: 'ngx-customers',
  styleUrls: ['./../company.style.scss'],
  templateUrl: 'customers.component.html',
})
export class CustomersComponent implements OnInit, OnDestroy {
  isRCDSS = environment.name === 'rcdss';
  loading: boolean = false;
  customer: Company;
  countries: any = [];
  taxTypes: any = [];
  priorities: any = [];
  customers$: Observable<Company[]>;
  @LocalStorage() organizationId: string;
  @LocalStorage() companyType: string;
  @ViewChild('grid', { static: true }) dataGrid: DxDataGridComponent;
  private subscriptions: Array<any> = [];
  customerRequiredFiles: CustomerRequiredFile[];

  constructor(
    private $company: CompanyService,
    private $shared: SharedService,
    private toaster: Toaster,
    private modalService: NgbModal,
    private broadcaster: Broadcaster,
    protected router: Router,
    protected $breakpoint: NbMediaBreakpointsService,
    protected $theme: NbThemeService,
  ) {
  }

  ngOnInit() {
    this.customers$ = this.$company.fetchCustomers(this.organizationId);
    this.taxTypes = this.$shared.taxTypes();
    this.priorities = this.$shared.priorities();
    // LISTEN FOR SEARCH EVENTS
    this.subscriptions.push(
      this.broadcaster.on(EventTypes.CUSTOMER_SEARCH_FINISHED).subscribe((data: Company[]) => {
        if (!!data) {
          this.customers$ = of(data);
        }
      }),
    );

    if (this.isRCDSS) {
      this.customers$.subscribe(customers => {
        from(customers).pipe(
          first(),
        ).subscribe((customer: Company) => {
          this.openCustomer({ data: {id: customer.id}});
        });
      })
    }

    // LISTEN FOR SEARCH EVENT WHEN SEARCH BAR IS CLEANED UP
    this.broadcaster
      .on(EventTypes.RESET_SEARCH)
      .subscribe(() => (this.customers$ = this.$company.fetchCustomers(this.organizationId)));

    this.$theme.onMediaQueryChange().subscribe(ts => {
      if (!this.dataGrid || !this.dataGrid.instance) {
        return;
      }
      this.dataGrid.instance.columnOption('priority', 'visible', !['xs', 'is', 'sm'].includes(ts[1].name));
      this.dataGrid.instance.columnOption('city', 'visible', !['xs', 'is', 'sm'].includes(ts[1].name));
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  openCustomer(event: any) {
    const customerId = event.data.id;
    this.$company
      .customer(this.organizationId, customerId)
      .subscribe(
        (customer) => {
          this.customer = customer;
          this.getCustomerRequiredFilesForSupplier();
        },
        (error) => {
          console.error(error);
          this.toaster.show('error', null, !!error.error ? error.error.detail : error.message);
        }
      );
  }

  updateCustomer() {
    this.$company
      .updateCustomer(this.organizationId, this.customer)
      .then()
      .catch(error => {
        console.error(error);
        this.toaster.show('error', null, !!error.error ? error.error.detail : error.message);
      });
  }

  customerPOs() {
    this.router.navigate(['suppliers', 'work-in-progress'], {
      queryParams: { customerId: this.customer.id },
    });
  }

  addCompany() {
    const activeModal = this.modalService.open(CreateCompanyModalComponent, {
      size: 'lg',
      container: 'nb-layout',
      windowClass: 'company-modal',
    });
    activeModal.componentInstance.company_as = 'customer';
    activeModal.result.then(_ => (this.customers$ = this.$company.fetchCustomers(this.organizationId)), () => null);
  }

  addFile($event, requirementId) {
    let file = $event.target.files[0];
    this.$company.addCustomerFile(
      this.customer.id,
      file,
      { 
        isInsuranceFile: true, 
        customerRequiredFileID: requirementId,
      },
    ).subscribe((res) => {
      this.toaster.show('success', 'Success', 'File added');
      this.customers$ = this.$company.fetchCustomers(this.organizationId);
      this.openCustomer({data: this.customer});
    }, (error) => {
      console.error(error);
      this.toaster.show('error', 'An error occurred', 'Please try again later.')
    });
  }

  onContentReadyHandler(event: any) {
    if (!this.dataGrid || !this.dataGrid.instance) {
      return;
    }
    const bp = this.$breakpoint.getByWidth(window.outerWidth);
    this.dataGrid.instance.columnOption('priority', 'visible', !['xs', 'is', 'sm'].includes(bp.name));
    this.dataGrid.instance.columnOption('city', 'visible', !['xs', 'is', 'sm'].includes(bp.name));
  }

  getCustomerRequiredFilesForSupplier() {
    if (!this.customer) {
      return;
    }
    this.$company.getCustomerRequiredFilesForSupplier(this.organizationId, this.customer.id)
      .subscribe((res) => {
        this.customerRequiredFiles = res;
      }, (error) => {
        console.error(error);
        this.toaster.show('error', 'Error', 'Failed to get Customer-Required Files.');
      });
  }
}
